import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query"; // Changed from useMutation to useQuery
import ProfileIcon from "./ProfileIcon";
import { useAppDispatch } from "@/store/store";
import { logOutSuccessAction } from "@/store/actions/login.actions";
import { TIME_CONFIG } from "@/utils/constant/global.constant";
import { logout } from "@/services/common/user.services";
import { toastMessage } from "@/utils/helper/myToast";

interface UserProfile {
  profilePic?: string;
  name: string;
}

interface LogoutModalProps {
  onClose: (props?: any) => void;
  userProfile: UserProfile;
}

interface CompletionStatus {
  api: boolean;
  progress: boolean;
}

const LogoutModal: React.FC<LogoutModalProps> = ({ onClose, userProfile }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [progress, setProgress] = useState<number>(0);
  const completionStatus = useRef<CompletionStatus>({
    api: false,
    progress: false,
  });

  const checkAndProceed = useCallback(() => {
    const { api, progress } = completionStatus.current;
    if (api && progress) {
      localStorage.setItem("userLogout", Date.now().toString());
      dispatch(logOutSuccessAction());
      navigate("/login");
    }
  }, [dispatch, navigate]);

  const handleLogoutSuccess = useCallback(
    (res: any) => {
      if (res.status === 200) {
        const { message, isError, showMessage } = res.data || {};
        if (showMessage) toastMessage(message, isError);
        completionStatus.current.api = true;
        checkAndProceed();
        return res;
      } else {
        const { message, isError } = res?.data || {};
        if (message) {
          toastMessage(message, isError);
        }
        onClose();
        return res;
      }
    },
    [checkAndProceed, onClose]
  );

  const { isError, error } = useQuery({
    queryKey: ["logout"],
    queryFn: logout,
    retry: 0,
    enabled: true,
    select: handleLogoutSuccess,
  });

  // Handle error state
  useEffect(() => {
    if (isError) {
      const errorMessage = (error as any)?.message || "Logout failed";
      toastMessage(errorMessage, true);
      onClose();
    }
  }, [isError, error, onClose]);

  // Handle progress animation
  useEffect(() => {
    const startTime = Date.now();
    const duration = TIME_CONFIG.S[2];
    let animationFrameId: number;

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);

      setProgress(newProgress);

      if (newProgress < 100) {
        animationFrameId = requestAnimationFrame(updateProgress);
      } else {
        completionStatus.current.progress = true;
        checkAndProceed();
      }
    };

    animationFrameId = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [checkAndProceed]);

  // Animation variants remain the same...
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modalVariants = {
    hidden: { scale: 0.8, opacity: 0, y: 20 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: { type: "spring", damping: 15, stiffness: 300 },
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      y: 20,
      transition: { duration: 0.2 },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0.3, duration: 0.5 },
    },
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Rest of your JSX remains the same */}
        <motion.div
          className="bg-white rounded-llg p-8 flex flex-col items-center max-w-md w-full"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div className="relative size-20">
            <motion.svg
              className="size-20 absolute"
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              <circle
                className="text-text-primary"
                strokeWidth="4"
                stroke="currentColor"
                fill="transparent"
                r="36"
                cx="40"
                cy="40"
                strokeDasharray={226.2}
                strokeDashoffset={226.2 - (226.2 * progress) / 100}
                strokeLinecap="round"
              />
            </motion.svg>
            {userProfile?.profilePic ? (
              <img src={userProfile.profilePic} alt="Profile" className="size-[78px] rounded-full object-cover" />
            ) : (
              <ProfileIcon name={userProfile.name} hideName className="!text-xl size-20" />
            )}
          </div>

          <motion.div variants={textVariants} initial="hidden" animate="visible" className="text-center mt-4">
            <motion.h2 className="text-2xl font-bold mb-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              Goodbye!
            </motion.h2>
            <motion.p className="text-text-default text-13 !leading-normal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
              Your documents are safe with us.
            </motion.p>
            <motion.span
              className="text-text-default text-10 block mt-1 !leading-normal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Thank you for using Lystproof — see you soon!
            </motion.span>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LogoutModal;
